import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { API_URL, resolveImageUrl } from '../config';
import './Footer.css';

// Custom inline SVG icons for brands (since lucide-react has removed brand icons in newer versions)
const InstagramIcon = ({ size = 18 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = ({ size = 18 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M18 2h-3a5 5 0 0 0 -5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const YoutubeIcon = ({ size = 18 }) => (
  <svg 
    viewBox="0 0 24 24" 
    width={size} 
    height={size} 
    stroke="currentColor" 
    strokeWidth="2" 
    fill="none" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
  </svg>
);

const defaultSettings = {
  logoTitle: "Himalayannuts",
  description: "Sourcing the finest, premium quality nuts, dry fruits, and lotus seeds directly from the foothills of the Himalayas. Pure, natural, and wholesome.",
  instagramUrl: "#instagram",
  facebookUrl: "#facebook",
  youtubeUrl: "#youtube"
};

const Footer = () => {
  const { branding } = useCart();
  const [email, setEmail] = useState('');
  const [settings, setSettings] = useState(defaultSettings);

  useEffect(() => {
    fetch(`${API_URL}/settings/footer`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load footer settings');
        return res.json();
      })
      .then((data) => {
        if (data && data.logoTitle !== undefined) {
          setSettings(data);
        }
      })
      .catch((err) => console.error('Error fetching footer settings:', err));
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log('Subscribed email:', email);
    alert('Thank you for subscribing to our newsletter!');
    setEmail('');
  };

  return (
    <footer className="footer-bg">
      <div className="container">
        <div className="footer-grid">
          
          {/* Column 1: Logo & Socials */}
          <div className="footer-col">
            <div className="footer-logo-wrapper">
              <div className="footer-logo-icon">
                <img 
                  src={resolveImageUrl(branding.logoUrl)} 
                  alt={`${branding.brandName} Logo`} 
                  className="footer-logo-img" 
                />
              </div>
              <span className="footer-logo-title">{branding.brandName}</span>
            </div>
            <p className="footer-desc">
              {settings.description}
            </p>
            <div className="social-links">
              <a href={settings.instagramUrl} className="social-icon-btn" aria-label="Instagram">
                <InstagramIcon size={18} />
              </a>
              <a href={settings.facebookUrl} className="social-icon-btn" aria-label="Facebook">
                <FacebookIcon size={18} />
              </a>
              <a href={settings.youtubeUrl} className="social-icon-btn" aria-label="YouTube">
                <YoutubeIcon size={18} />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-col">
            <h3 className="footer-col-title">Quick Links</h3>
            <ul className="footer-links-list">
              <li><a href="#shop">Shop Products</a></li>
              <li><Link to="/about-us">About Our Brand</Link></li>
              <li><a href="#why-us">Why Himalayan Nuts</a></li>
              <li><Link to="/blog">Healthy Living Blog</Link></li>
              <li><Link to="/contact">Contact Support</Link></li>
            </ul>
          </div>

          {/* Column 3: Categories */}
          <div className="footer-col">
            <h3 className="footer-col-title">Categories</h3>
            <ul className="footer-links-list">
              <li><a href="#almonds">Premium Nuts</a></li>
              <li><a href="#makhana">Himalayan Makhana</a></li>
              <li><a href="#seeds">Nutrient Seeds</a></li>
              <li><a href="#raisins">Golden Raisins</a></li>
              <li><a href="#combo-offers">Combo Packs</a></li>
            </ul>
          </div>

          {/* Column 4: Customer Care */}
          <div className="footer-col">
            <h3 className="footer-col-title">Customer Care</h3>
            <ul className="footer-links-list">
              <li><Link to="/track">Track My Order</Link></li>
              <li><a href="#shipping">Shipping Policy</a></li>
              <li><a href="#returns">Returns & Refunds</a></li>
              <li><a href="#terms">Terms & Conditions</a></li>
              <li><a href="#privacy">Privacy Policy</a></li>
            </ul>
          </div>

          {/* Column 5: Newsletter */}
          <div className="footer-col">
            <h3 className="footer-col-title">Newsletter</h3>
            <p className="newsletter-desc">
              Subscribe to get special discounts, free recipes, and healthy eating tips.
            </p>
            <form onSubmit={handleSubscribe} className="newsletter-form">
              <div className="newsletter-input-wrapper">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="newsletter-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="newsletter-subscribe-btn">
                <span>Subscribe Now</span>
              </button>
            </form>
          </div>

        </div>

        {/* Divider */}
        <div className="footer-bottom-divider" />

        {/* Bottom copyright and payment icons */}
        <div className="footer-bottom-row">
          <span>&copy; {new Date().getFullYear()} Himalayannuts. All Rights Reserved.</span>
          <div className="payment-methods-logos">
            <span className="payment-badge">VISA</span>
            <span className="payment-badge">Mastercard</span>
            <span className="payment-badge">UPI</span>
            <span className="payment-badge">Paytm</span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
